import EntryPortal from "./EntryPortal";
import LegalFooter from "./LegalFooter";
import {useBillingConfig} from "./BillingAccess";
import "./entry-auth.css";
import "./immersive-public.css";
import { useState, useEffect, useRef } from "react";
import {
  signInWithPopup,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth, googleProvider } from "./firebase";
import Brand from "./Brand";
import AccountSecurity from "./AccountSecurity";
import { authError } from "./auth-security";
import { sendResetEmail } from './auth-email';
import { colors as C } from "./appearance";

// ─── Design tokens — mirrors the app exactly ─────────────────────────────────
const mono = "var(--tl-font)";
const cond = "var(--tl-heading)";

const ease = "cubic-bezier(0.16,1,0.3,1)";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function PanelCurve() {
  return (
    <svg viewBox="0 0 400 90" style={{ width: "100%", height: 72, display: "block", opacity: 0.5 }}>
      <defs><linearGradient id="lpArea" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#4ade80" stopOpacity="0.18" /><stop offset="100%" stopColor="#4ade80" stopOpacity="0" />
      </linearGradient></defs>
      <path d="M 0 74 C 30 70 45 62 70 64 C 95 66 105 50 130 52 C 155 54 170 38 200 40 C 230 42 245 28 275 26 C 305 24 325 16 360 12 L 400 8 L 400 90 L 0 90 Z" fill="url(#lpArea)" />
      <path d="M 0 74 C 30 70 45 62 70 64 C 95 66 105 50 130 52 C 155 54 170 38 200 40 C 230 42 245 28 275 26 C 305 24 325 16 360 12 L 400 8" fill="none" stroke="#4ade80" strokeWidth="1.5" strokeLinecap="round" pathLength="1" style={{ strokeDasharray: 1, strokeDashoffset: 1, animation: `lpDraw 1.6s ${ease} 0.5s both` }} />
    </svg>
  );
}

function Spinner({ dark }) {
  return <span style={{ width: 13, height: 13, border: `2px solid ${dark ? "rgba(0,0,0,0.25)" : C.s4}`, borderTopColor: dark ? "var(--tl-button-text)" : C.win, borderRadius: "50%", display: "inline-block", animation: "lpSpin 0.7s linear infinite", verticalAlign: -2 }} />;
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function LoginPage({ preview = false, user: pendingUser, onBack, initialMode = "signin" }) {
  // Verification is handled by the same account screen after every sign-in.
  const billing=useBillingConfig();
  const [mode, setMode] = useState(pendingUser ? "verify" : initialMode);
  const [email, setEmail] = useState(pendingUser?.email || "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const actionLock = useRef(false);

  useEffect(() => { const t = setTimeout(() => setVisible(true), 60); return () => clearTimeout(t); }, []);

  const fail = msg => { setError(msg); setLoading(false); };
  const clearBanners = () => { setError(""); setNotice(""); };

  // ── Auth actions ────────────────────────────────────────────────────────────
  const handleGoogle = async () => {
    if(preview){setNotice("This is a design preview. Sign in on the live site to access your account.");return;}
    if(actionLock.current)return;actionLock.current=true;setLoading(true); clearBanners();
    try { await signInWithPopup(auth, googleProvider); } // Google emails arrive pre-verified
    catch(e) { fail(authError(e)); }
    finally {actionLock.current=false;setLoading(false);}
  };

  const validate = () => {
    if (!EMAIL_RE.test(email.trim())) return "That doesn't look like a valid email.";
    if (!password) return "Enter your password.";
    return null;
  };

  const handleSignIn = async () => {
    if(preview){setNotice("This is a design preview. Your real account has not been signed in.");return;}
    const v = validate(); if (v) return fail(v);
    if(actionLock.current)return;actionLock.current=true;setLoading(true);clearBanners();
    try { await signInWithEmailAndPassword(auth,email.trim(),password); }
    catch(e) {fail(authError(e));}
    finally {actionLock.current=false;setLoading(false);}
  };
  const handleReset = async () => {
    if(preview){setNotice("This is a design preview. No reset email will be sent.");return;}
    if (!EMAIL_RE.test(email.trim())) return fail("Enter your email above first.");
    if(actionLock.current)return;actionLock.current=true;
    setLoading(true); clearBanners();
    try {
      await sendResetEmail(email.trim());
      setMode("reset-sent");
    } catch (e) {
      if(e.code === "auth/user-not-found") setMode("reset-sent");
      else setError(authError(e));
    } finally { actionLock.current=false; setLoading(false); }

  };

  const switchTo = m => { setMode(m); clearBanners(); };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const inputSt = { width: "100%", background: C.s2, border: `1px solid ${C.s4}`, borderRadius: 8, padding: "13px 15px", color: C.t1, fontSize: 13, fontFamily: mono, outline: "none", boxSizing: "border-box", letterSpacing: "0.02em", transition: `border-color 0.2s ${ease}` };
  const labelSt = { display: "block", fontSize: 12, color: C.t4, fontFamily: mono, letterSpacing: "0.16em", marginBottom: 7 };
  const linkBtn = { background: "none", border: "none", color: C.t4, fontSize: 12, fontFamily: mono, cursor: "pointer", letterSpacing: "0.1em", padding: 0, transition: "color 0.15s" };
  const primaryBtn = { width: "100%", padding: "13px 0", background: "var(--tl-accent)", opacity: loading ? .65 : 1, color: "var(--tl-button-text)", border: "none", borderRadius: 9, fontSize: 12, fontFamily: mono, letterSpacing: "0.14em", fontWeight: 700, cursor: loading ? "wait" : "pointer", transition: `all 0.2s ${ease}`, display: "flex", alignItems: "center", justifyContent: "center", gap: 9 };
  const fade = d => ({ opacity: visible ? 1 : 0, transform: visible ? "none" : "translateY(16px)", transition: `opacity 0.6s ${d}s ${ease}, transform 0.6s ${d}s ${ease}` });
  const hover = (on, off) => ({ onMouseEnter: e => (e.currentTarget.style.color = on), onMouseLeave: e => (e.currentTarget.style.color = off) });

  const banners = (
    <>
      {error && <div role="alert" className="lp-shake" style={{ fontSize: 12, color: C.loss, background: "rgba(248,113,113,0.07)", border: "1px solid rgba(248,113,113,0.22)", borderRadius: 8, padding: "10px 13px", fontFamily: mono, lineHeight: 1.5 }}>{error}</div>}
      {notice && <div role="status" style={{ fontSize: 12, color: C.amber, background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 8, padding: "10px 13px", fontFamily: mono, lineHeight: 1.5 }}>{notice}</div>}
    </>
  );

  // ── Screens ─────────────────────────────────────────────────────────────────
  const emailField = (
    <div>
      <label htmlFor="lp-email" style={labelSt}>EMAIL</label>
      <input id="lp-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com"
        type="email" autoComplete="email" style={inputSt}
        onKeyDown={e => e.key === "Enter" && (mode === "reset" ? handleReset() : handleSignIn())} />
    </div>
  );

  const passwordField = (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <label htmlFor="lp-password" style={labelSt}>PASSWORD</label>
        {mode === "signin" && <button onClick={() => switchTo("reset")} style={{ ...linkBtn, color: C.t5, marginBottom: 7 }} {...hover(C.t3, C.t5)}>FORGOT?</button>}
      </div>
      <div style={{ position: "relative" }}>
        <input id="lp-password" value={password} onChange={e => setPassword(e.target.value)}
          placeholder="••••••••"
          type={showPassword ? "text" : "password"}
          autoComplete="current-password"
          style={{ ...inputSt, paddingRight: 52 }}
          onKeyDown={e => e.key === "Enter" && handleSignIn()} />
        <button onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? "Hide password" : "Show password"}
          style={{ position: "absolute", right: 13, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: showPassword ? C.win : C.t5, fontFamily: mono, fontSize: 12, letterSpacing: "0.1em", padding: 0 }}>
          {showPassword ? "HIDE" : "SHOW"}
        </button>
      </div>

    </div>
  );

  const googleBtn = (
    <button onClick={handleGoogle} disabled={loading} className="lp-google"
      style={{ width: "100%", padding: "12px 0", background: C.s2, border: `1px solid ${C.s4}`, borderRadius: 9, color: C.t1, fontSize: 12, fontFamily: mono, letterSpacing: "0.1em", cursor: loading ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, transition: `all 0.2s ${ease}` }}>
      <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
      </svg>
      {loading ? "PLEASE WAIT..." : "CONTINUE WITH GOOGLE"}
    </button>
  );

  const divider = (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "18px 0" }}>
      <div style={{ flex: 1, height: 1, background: C.s3 }} />
      <span style={{ fontSize: 12, color: C.t5, fontFamily: mono, letterSpacing: "0.16em" }}>OR</span>
      <div style={{ flex: 1, height: 1, background: C.s3 }} />
    </div>
  );

  let screen;
  if (mode === "verify") {
    screen = <AccountSecurity user={pendingUser || auth.currentUser} onRefresh={()=>window.location.reload()}/>;
  } else if (mode === "reset" || mode === "reset-sent") {
    screen = mode === "reset" ? (
      <>
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontFamily: cond, fontSize: 22, fontWeight: 600, letterSpacing: "0.04em", color: C.t1 }}>Password or invitation</div>
          <div style={{ fontSize: 12, color: C.t4, fontFamily: mono, marginTop: 5, lineHeight: 1.7 }}>Already a member or approved by L? We’ll email you a secure link to reset your password or finish setting up your account.</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {emailField}
          {banners}
          <button onClick={handleReset} disabled={loading} style={primaryBtn}>{loading ? <><Spinner dark /> SENDING…</> : "SEND RESET LINK"}</button>
          <button onClick={() => switchTo("signin")} style={{ ...linkBtn, textAlign: "center" }} {...hover(C.t2, C.t4)}>← BACK TO SIGN IN</button>
        </div>
      </>
    ) : (
      <div style={{ textAlign: "center", padding: "10px 0 4px" }}>
        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(74,222,128,0.1)", border: "1.5px solid rgba(74,222,128,0.4)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.win} strokeWidth="2" strokeLinecap="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" /></svg>
        </div>
        <div style={{ fontFamily: cond, fontSize: 21, fontWeight: 600, letterSpacing: "0.05em", color: C.t1 }}>Check your inbox</div>
        <div style={{ fontSize: 12, color: C.t4, fontFamily: mono, marginTop: 8, lineHeight: 1.8 }}>
          If this email has an account or approved access, <span style={{ color: C.t2 }}>{email}</span> will receive a secure link. Check your spam folder too.
        </div>
        <button onClick={() => switchTo("signin")} style={{ ...linkBtn, marginTop: 20 }} {...hover(C.t2, C.t4)}>← BACK TO SIGN IN</button>
      </div>
    );
  } else if (mode === "signup") {
    screen = <><Brand size={24}/><div style={{marginTop:22,marginBottom:24}}><span style={labelSt}>NEW MEMBERS</span><h1 style={{fontFamily:cond,fontSize:30,lineHeight:1.1,margin:'8px 0 14px'}}>{billing.enabled?'Your next chapter.':'Free registration has closed.'}</h1><p style={{fontSize:13,color:C.t3,lineHeight:1.8}}>{billing.enabled?'One complete journal. £19.99 GBP per month, recurring until cancelled.':'The free member limit has been reached. New access will be paid.'}</p><p style={{fontSize:13,color:C.t3,lineHeight:1.8}}>{billing.enabled?'Confirm payment securely with Stripe, then check your email for account setup. Existing free-forever members do not need to pay.':'Leave your email or social handle with TradeLog Support. L will respond with the details. Our existing members keep their free-forever access.'}</p></div><a href={billing.enabled?"/checkout":"/support?type=access"} className="tl-btn tl-btn-primary" style={{...primaryBtn,textDecoration:'none'}}>{billing.enabled?"GET TRADELOG":"REQUEST PAID ACCESS"}</a><p style={{fontSize:11,color:C.t4,lineHeight:1.7,margin:'14px 0 22px'}}>{billing.enabled?'You can review the full terms before payment. Cancel future renewals through your account.':'No payment is taken by sending a request. Payments are not open yet.'}</p><button onClick={()=>switchTo('signin')} style={{...linkBtn,width:'100%',textAlign:'center'}}>ALREADY A MEMBER? SIGN IN</button></>;
  } else {
    screen = (
      <>
        <div style={{ marginBottom: 26 }}>
          <div style={{ marginBottom: 12 }}><Brand size={24} /></div>
          <div style={{ fontFamily: cond, fontSize: 22, fontWeight: 600, letterSpacing: "0.04em", color: C.t1 }}>Welcome back</div>
          <div style={{ fontSize: 12, color: C.t4, fontFamily: mono, marginTop: 5, lineHeight: 1.6 }}>Sign in to continue to your journal.</div>
        </div>
        {googleBtn}
        {divider}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {emailField}
          {passwordField}
          {banners}
          <button onClick={handleSignIn} disabled={loading} className="lp-primary" style={{ ...primaryBtn, marginTop: 2 }}>
            {loading ? <><Spinner dark /> PLEASE WAIT…</> : "SIGN IN"}
          </button>
          <button onClick={() => switchTo("signup")} style={{ ...linkBtn, textAlign: "center", marginTop: 2 }} {...hover(C.t2, C.t4)}>
            NEW TO TRADELOG? REQUEST PAID ACCESS
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="tl-auth tl-auth-experience" style={{ position: "fixed", inset: 0, background: C.bg, color: C.t1, display: "flex", fontFamily: mono, overflow: "auto" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700&family=DM+Mono:wght@300;400;500&display=swap');
        .tl-auth *{box-sizing:border-box;}
        @keyframes lpDraw{to{stroke-dashoffset:0}}
        @keyframes lpAurora{0%,100%{transform:translate(0,0) scale(1);opacity:0.6}50%{transform:translate(6%,4%) scale(1.14);opacity:0.85}}
        @keyframes lpSpin{to{transform:rotate(360deg)}}
        @keyframes lpPop{from{transform:scale(0.5);opacity:0}to{transform:scale(1);opacity:1}}
        @keyframes lpCheck{to{stroke-dashoffset:0}}
        @keyframes lpShakeK{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-5px)}40%,80%{transform:translateX(5px)}}
        .lp-shake{animation:lpShakeK 0.35s ease;}
        .tl-auth input:focus{border-color:rgba(74,222,128,0.55)!important;background:rgba(74,222,128,0.03)!important;}
        .tl-auth input::placeholder{color:rgba(255,255,255,0.22);}
        .tl-auth button{-webkit-tap-highlight-color:transparent;touch-action:manipulation;font-family:inherit;}
        .tl-auth button:active:not(:disabled){transform:scale(0.98);}
        .lp-primary{position:relative;overflow:hidden;}
        .lp-primary::before{content:'';position:absolute;top:0;left:-60%;width:40%;height:100%;background:linear-gradient(100deg,transparent,rgba(255,255,255,0.5),transparent);transform:skewX(-20deg);transition:left 0.5s ease;}
        .lp-primary:hover::before{left:130%;}
        .lp-primary:hover:not(:disabled){box-shadow:0 8px 28px rgba(74,222,128,0.3);}
        .lp-google:hover{background:#16161a!important;border-color:#3a3a44!important;}
        .tl-auth ::-webkit-scrollbar{width:0;}
        @media(max-width:760px){.lp-left{display:none!important;}}
        @media(prefers-reduced-motion:reduce){.tl-auth *{animation-duration:0.01ms!important;transition-duration:0.1ms!important;}}
      `}</style>

      <div className="lp-left" style={{ opacity: visible ? 1 : 0, transition: `opacity 0.6s ${ease}`, flex: "1 1 46%", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "44px 52px", borderRight: `1px solid ${C.s3}`, background: C.s1, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -140, left: -80, width: 460, height: 420, background: "radial-gradient(ellipse, rgba(74,222,128,0.12), transparent 65%)", filter: "blur(20px)", pointerEvents: "none", animation: "lpAurora 16s ease-in-out infinite" }} />
        <div style={{ position: "absolute", inset: 0, opacity: 0.5, backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "34px 34px", maskImage: "radial-gradient(ellipse 90% 70% at 20% 20%, black, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse 90% 70% at 20% 20%, black, transparent 70%)", pointerEvents: "none" }} />
        <div className="tl-auth-brand" style={{ position: "relative" }}><Brand size={28} /><span>YOUR NEXT CHAPTER</span></div>
        <EntryPortal className="tl-auth-entry-scene"/>
        <div className="lp-identity-copy" style={{ maxWidth: 380, position: "relative" }}>
          <div style={{ fontFamily: cond, fontSize: "clamp(34px,4.2vw,52px)", fontWeight: 700, letterSpacing: "0.04em", lineHeight: 1.02, marginBottom: 20 }}>
            Your world.<br /><span style={{ color: C.win }}>Your flow.</span>
          </div>
          <div className="lp-benefits" style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 36 }}>
            {["Keep every decision and the lesson behind it", "See your performance in a new perspective", "Give your progress a place to belong"].map(t => (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 11 }}>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: C.win, flexShrink: 0 }} />
                <span style={{ fontSize: 12, color: C.t3, fontFamily: mono }}>{t}</span>
              </div>
            ))}
          </div>
          <PanelCurve />
        </div>
        <div style={{ fontSize: 12, color: C.t5, fontFamily: mono, letterSpacing: "0.12em", position: "relative" }}>trade-log.co.uk</div>
      </div>

      <div className="lp-form-pane" style={{ flex: "1 1 54%", minWidth: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: "32px 24px" }}>
        <div className="lp-form-wrap" style={{ ...fade(0.08), width: "100%", maxWidth: 380 }}>
          <div className="tl-auth-mobile-brand"><Brand/></div>
          {onBack && !pendingUser && (
            <button onClick={onBack} style={{ ...linkBtn, display: "flex", alignItems: "center", gap: 6, marginBottom: 18 }} {...hover(C.t2, C.t4)}>← BACK</button>
          )}
          <div className="lp-form-card" style={{ background: C.s1, border: `1px solid ${C.s3}`, borderRadius: 14, padding: "30px 28px 26px", position: "relative" }}>
            <div key={mode} className="lp-screen">{screen}</div>
          </div>
          <div style={{ textAlign: "center", marginTop: 16 }}>
            <span style={{ fontSize: 12, color: C.t5, fontFamily: mono, letterSpacing: "0.1em" }}>YOUR JOURNAL · YOUR PROCESS · YOUR PROGRESS</span>
          </div>
          <LegalFooter/>
          <p style={{ textAlign: "center", fontSize: 11, marginTop: 20, color: C.t3, lineHeight: 1.8 }}>
            <a href="/support" style={{ color: "inherit" }}>Need help signing in?</a>
          </p>
        </div>
      </div>
</div>
  );
}
